/* Modal primitive, wrapping native <dialog>.

   The platform gives focus trapping, Escape-to-close, backdrop rendering, inert
   background content and top-layer stacking — all of which are easy to
   reimplement badly. Same reasoning as g-button wrapping <button>.

   Per §31 (Grain should feel quiet) modals are reserved for irreversible
   actions; the only current caller is category deletion. */

import { LitElement, html, css } from 'lit';
import { shadowReset } from '../../design/base.js';
import { customElement, property, query } from 'lit/decorators.js';

@customElement('g-dialog')
export class GDialog extends LitElement {
  static override styles = [
    shadowReset,
    css`
      dialog {
        width: min(420px, calc(100vw - 2 * var(--space-4)));
        padding: var(--space-5);
        border: 1px solid var(--border);
        border-radius: var(--radius-lg);
        background: var(--surface);
        color: var(--text);
        font-family: var(--font-ui);
      }
      dialog::backdrop {
        background: rgb(0 0 0 / 0.45);
      }
      h2 {
        margin: 0 0 var(--space-2);
        font-family: var(--font-display);
        font-size: 1.1rem;
        font-weight: 700;
      }
      .actions {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-2);
        margin-top: var(--space-5);
      }
    `,
  ];

  @property() heading = '';
  @query('dialog') private el!: HTMLDialogElement;

  /** Opens as a true modal (focus trap + inert background). */
  show(): void {
    this.el?.showModal();
  }

  close(): void {
    this.el?.close();
  }

  override render() {
    return html`
      <dialog
        aria-label=${this.heading}
        @close=${() =>
          this.dispatchEvent(new CustomEvent('g-close', { bubbles: true, composed: true }))}
      >
        ${this.heading ? html`<h2>${this.heading}</h2>` : ''}
        <slot></slot>
        <div class="actions"><slot name="actions"></slot></div>
      </dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-dialog': GDialog;
  }
}
